import { Lightbulb } from '@mui/icons-material';
import React, { useCallback, useMemo, useState } from 'react';
import {
  SubprojectBuildingDetails,
  TechnologyParameter,
} from '../../stores/SubprojectStore.ts';
import { DecarbContentLayout } from '../components/DecarbContentLayout.tsx';
import { DecarbOverviewHeader } from '../components/DecarbOverviewHeader.tsx';
import { FilterMenu, SearchBox, SortMenu } from '../components/TechToolbar';
import {
  EnergyKind,
  LightingWithMetrics,
  Tab,
  TechSortDimensions,
  TechSortState,
  sortTechItemsByState,
} from '../types';
import { isPassiveTechnology, storedCategoryCapitalCost } from '../utils.ts';
import { calculateEnergyDemand } from '../utils/calculations';
import { LightingUnit } from './LightingUnit.tsx';

interface LightingForBuildingDetailsProps {
  subproject: SubprojectBuildingDetails;
  // The stored cost figures predate the selections on screen (unsaved edit).
  costsArePending?: boolean;
  tab: Tab;
  lightingSystems: LightingWithMetrics[];
  onAddLighting: (lightingId: string, limit: number) => void;
  onChangeLightingCount: (lightingId: string, newCount: number) => void;
  onCopyConventionalAlternative: (tab: Tab) => void;
  emptyText: React.ReactNode;
  isLoading?: boolean;
  readOnly?: boolean;
}

export const LightingForBuildingDetails: React.FC<
  LightingForBuildingDetailsProps
> = ({
  subproject,
  costsArePending = false,
  tab,
  lightingSystems,
  onAddLighting,
  onChangeLightingCount,
  emptyText,
  isLoading,
  readOnly,
}) => {
  const buildingAlternative = subproject[tab];
  const selectedParams: TechnologyParameter[] = useMemo(
    () => buildingAlternative?.technologyParams['lighting']?.params || [],
    [buildingAlternative],
  );

  const [searchString, setSearchString] = useState('');
  const [sortState, setSortState] = useState<TechSortState>({
    dimension: TechSortDimensions[0],
    direction: 'asc',
  });
  const [filter, setFilter] = useState<EnergyKind[]>([]);

  const availableEnergyTypes = useMemo(() => {
    const types = new Set(
      lightingSystems.map((l) => l.energySink).filter(Boolean),
    );
    return [...types] as EnergyKind[];
  }, [lightingSystems]);

  // Filter available lighting based on current alternative and what's already selected
  const filterByAlternative = useCallback(
    (item: LightingWithMetrics): boolean => {
      // Conventional: All technologies available
      if (tab === 'conventionalAlternative') {
        return true;
      }

      // Low Carbon: No gas
      if (tab === 'reducedCarbonAlternative') {
        return !item.energySink?.includes('Gas');
      }

      // Net Zero: No gas AND only low-energy electricity (≤2 icons)
      if (tab === 'zeroCarbonAlternative') {
        if (item.energySink?.includes('Gas')) {
          return false;
        }
        if (
          item.energySink === 'Electricity' &&
          item.relativeEnergyDemand > 2
        ) {
          return false;
        }
        return true;
      }

      return true;
    },
    [tab],
  );

  const availableLightingSystems = useMemo(() => {
    const filtered = lightingSystems.filter((lighting) => {
      // Filter out already selected items
      const isSelected = selectedParams.some(
        (param) => param.techId === String(lighting.id),
      );
      if (isSelected) {
        return false;
      }
      // Filter by alternative constraints
      if (!filterByAlternative(lighting)) {
        return false;
      }
      // Search filter
      if (
        searchString.length > 0 &&
        !lighting.name.toLowerCase().includes(searchString.toLowerCase())
      ) {
        return false;
      }
      // Energy source filter (include only selected energy types)
      if (filter.length > 0 && !filter.includes(lighting.energySink)) {
        return false;
      }
      return true;
    });
    return sortTechItemsByState(filtered, sortState);
  }, [
    lightingSystems,
    selectedParams,
    filterByAlternative,
    searchString,
    filter,
    sortState,
  ]);

  const selectedLightingSystems = useMemo(() => {
    return selectedParams
      .map((param) => {
        const lighting = lightingSystems.find(
          (l) => String(l.id) === param.techId,
        );
        if (!lighting) {
          return null;
        }
        return { ...lighting, count: param.units };
      })
      .filter((item) => item !== null);
  }, [selectedParams, lightingSystems]);

  // Calculate coverage, cost, and energy
  const metrics = useMemo(() => {
    // Capital comes from the stored category total, never local arithmetic
    // -- see storedCategoryCapitalCost for why. The tab badge reads the same.
    const cost = storedCategoryCapitalCost(
      subproject[tab],
      'lighting',
      costsArePending,
    );

    if (selectedLightingSystems.length === 0) {
      return { cost, energyDemand: 0, systemCoveragePercentage: 0 };
    }

    const energyDemand = selectedLightingSystems.reduce(
      (acc, item) =>
        acc + calculateEnergyDemand(item.energyUsePerYr, item.count),
      0,
    );

    const systemCoverageArea = selectedLightingSystems.reduce((acc, item) => {
      // Passive technologies have 0% coverage
      if (isPassiveTechnology(item)) {
        return acc;
      }
      // Coverage calculation: count / limit gives the fraction covered
      return acc + item.count / Math.max(1, item.limit);
    }, 0);

    return {
      cost,
      energyDemand,
      systemCoveragePercentage: systemCoverageArea,
    };
  }, [selectedLightingSystems, subproject, tab, costsArePending]);

  return (
    <DecarbContentLayout
      availableItems={
        readOnly
          ? []
          : availableLightingSystems.map((item) => (
              <LightingUnit
                key={item.id}
                lightingUnit={item}
                selected={false}
                actions={{
                  onAdd: () =>
                    onAddLighting(String(item.id), Number(item.limit)),
                }}
              />
            ))
      }
      selectedItems={selectedLightingSystems.map((item) => (
        <LightingUnit
          key={item.id}
          count={item.count}
          lightingUnit={item}
          selected={true}
          isLoading={isLoading}
          readOnly={readOnly}
          actions={{
            onIncrement: () =>
              onChangeLightingCount(String(item.id), item.count + 1),
            onDecrement: () =>
              onChangeLightingCount(String(item.id), item.count - 1),
            onRemove: () => onChangeLightingCount(String(item.id), 0),
            onChangeCount: (newCount: number) =>
              onChangeLightingCount(String(item.id), newCount),
          }}
        />
      ))}
      overviewHeader={
        <DecarbOverviewHeader
          systemCoverageName={'Lighting'}
          systemCoverageIcon={<Lightbulb />}
          systemCoverageHelperText={
            'Choose enough lighting to get 100% coverage'
          }
          energyDemand={metrics.energyDemand}
          capitalCost={metrics.cost}
          coveragePercentage={metrics.systemCoveragePercentage}
        />
      }
      helperText={emptyText}
      searchComponent={
        readOnly ? undefined : (
          <SearchBox query={searchString} setQuery={setSearchString} />
        )
      }
      filterComponent={
        readOnly ? undefined : availableEnergyTypes.length > 1 ? (
          <FilterMenu
            listOfAllOptions={availableEnergyTypes}
            selectedOptions={filter}
            onReset={() => setFilter([])}
            onSelect={(option: EnergyKind) =>
              setFilter((prev) =>
                prev.includes(option)
                  ? prev.filter((val) => val !== option)
                  : [...prev, option],
              )
            }
          />
        ) : undefined
      }
      sortComponent={
        readOnly ? undefined : (
          <SortMenu
            dimensions={TechSortDimensions}
            activeSort={sortState}
            onSortChange={setSortState}
          />
        )
      }
    />
  );
};
